import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE } from '../config';
import { TagIcon } from './IconSet';
import { useToast } from '../context/ToastContext';
import './AdBanner.css';

const MAX_UNITS = 4;
const POLL_INTERVAL_MS = 10 * 60 * 1000;
const FADE_MS = 250;
const AUTO_ADVANCE_MS = 4500;

function AdBanner({ slot = 'sidebar', smoothScroll = true }) {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [ads, setAds] = useState([]);
  const [visible, setVisible] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const hasLoadedOnce = useRef(false);
  const rowRef = useRef(null);
  const isInteractingRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    let intervalId;

    const load = async () => {
      try {
        const res = await fetch(
          `${API_BASE}/ads/banners?slot=${slot}&limit=${MAX_UNITS}`,
        );
        if (!res.ok) {return;}
        const json = await res.json();
        const data = Array.isArray(json) ? json : [];
        if (cancelled) {return;}

        if (!hasLoadedOnce.current) {
          setAds(data);
          setLoaded(true);
          hasLoadedOnce.current = true;
          return;
        }

        setVisible(false);
        setTimeout(() => {
          if (cancelled) {return;}
          setAds(data);
          setVisible(true);
        }, FADE_MS);
      } catch {
        // Silently ignore. The strip just stays as-is until the next poll.
      }
    };

    load();
    intervalId = setInterval(load, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [slot]);

  // Auto-advance so every paid placement gets guaranteed display time,
  // instead of relying on a visitor choosing to scroll the row themselves.
  useEffect(() => {
    if (ads.length <= 1) {
      return undefined;
    }
    const prefersReducedMotion = window.matchMedia?.(
      '(prefers-reduced-motion: reduce)',
    ).matches;

    const intervalId = setInterval(() => {
      const row = rowRef.current;
      if (!row || isInteractingRef.current) {
        return;
      }
      const firstUnit = row.firstElementChild;
      const step = firstUnit
        ? firstUnit.getBoundingClientRect().width + parseFloat(getComputedStyle(row).gap || '0')
        : row.clientWidth;
      const atEnd = row.scrollLeft + row.clientWidth >= row.scrollWidth - 1;
      // Wrapping to the first ad must never look like a rewind - jump back
      // instantly (no animation) so the only motion a visitor ever sees is
      // forward, same as every other advance to "the next" ad.
      row.scrollTo({
        left: atEnd ? 0 : row.scrollLeft + step,
        behavior: atEnd || prefersReducedMotion || !smoothScroll ? 'auto' : 'smooth',
      });
    }, AUTO_ADVANCE_MS);

    return () => clearInterval(intervalId);
  }, [ads, smoothScroll]);

  const pauseAutoAdvance = () => {
    isInteractingRef.current = true;
  };

  const resumeAutoAdvance = () => {
    isInteractingRef.current = false;
  };

  const handleAdClick = (ad) => {
    if (!ad.is_default) {
      fetch(`${API_BASE}/ads/${ad.id}/click`, { method: 'POST' }).catch(() => {});
    }
    if (ad.click_url) {
      window.open(ad.click_url, '_blank', 'noopener,noreferrer');
    }
  };

  if (!loaded || ads.length === 0) {
    return null;
  }

  return (
    <aside className="ad-banner" aria-label="Sponsored content">
      <span className="ad-banner__tag">
        <TagIcon size={11} />
        Sponsored
      </span>
      <div
        ref={rowRef}
        className={`ad-banner__row${visible ? '' : ' ad-banner__row--fading'}`}
        onMouseEnter={pauseAutoAdvance}
        onMouseLeave={resumeAutoAdvance}
        onTouchStart={pauseAutoAdvance}
        onTouchEnd={resumeAutoAdvance}
        onFocus={pauseAutoAdvance}
        onBlur={resumeAutoAdvance}
      >
        {ads.map((ad) => (
          <button
            key={ad.id}
            type="button"
            className="ad-banner__unit"
            onClick={() => handleAdClick(ad)}
          >
            {ad.image_url && (
              <img
                className="ad-banner__image"
                src={ad.image_url}
                alt={ad.title || 'Sponsored'}
                loading="lazy"
              />
            )}
            <div className="ad-banner__body">
              <p className="ad-banner__title">{ad.title}</p>
              {ad.description && <p className="ad-banner__desc">{ad.description}</p>}
              <p className="ad-banner__cta">Visit site →</p>
            </div>
          </button>
        ))}
      </div>

      <button
        type="button"
        className="ad-banner__advertise-fab"
        onClick={() => {
          showToast("You've got a quick form to fill out. Takes about 2 minutes.", 'info');
          navigate('/advertise');
        }}
      >
        + Advertise
      </button>
    </aside>
  );
}

export default AdBanner;
